import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import Pagination from "../components/Pagination";
import {
  canShowReceipt,
  customerStatusLabel,
  formatDateTime,
  formatMoney,
  orderItems,
} from "../utils/orderHelpers";

const PAGE_SIZE = 8;

export default function UserOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;

      if (!user) {
        setSignedIn(false);
        setLoading(false);
        return;
      }
      setSignedIn(true);

      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      // The email filter matters: admins can read every order, but this page is "my" orders.
      // Abandoned or failed checkouts are hidden so customers only see real orders.
      const { data, count, error: queryError } = await supabase
        .from("orders")
        .select("*", { count: "exact" })
        .eq("email", user.email)
        .not("status", "in", "(cancelled,failed)")
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .range(from, to);

      if (cancelled) return;

      if (queryError) {
        if (queryError.code === "PGRST103" && page > 1) {
          setPage(1);
          return;
        }
        console.error(queryError);
        setError("We couldn't load your orders. Please try again.");
        setOrders([]);
        setTotal(0);
      } else {
        setOrders(data ?? []);
        setTotal(count ?? 0);
      }
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loading && orders.length === 0) {
    return (
      <div className="ord">
        <h2 className="ord-title">My orders</h2>
        <p className="adm-muted">Loading your orders...</p>
      </div>
    );
  }

  if (!signedIn) {
    return (
      <div className="ord">
        <h2 className="ord-title">My orders</h2>
        <div className="shop-empty">
          <h3>Sign in to see your orders</h3>
          <p>Your order history and receipts are kept with your account.</p>
          <Link to="/login" className="ui-btn ui-btn--primary">Sign in</Link>
          <p style={{ marginTop: "1rem" }}>
            Ordered as a guest? <Link to="/order-lookup">Find your order</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ord">
      <h2 className="ord-title">My orders</h2>
      <p className="ord-sub">Your order history, and a receipt for every paid order.</p>

      {error && <p className="ui-alert ui-alert--error">{error}</p>}

      {!error && orders.length === 0 && (
        <div className="shop-empty">
          <h3>No orders yet</h3>
          <p>When you place an order, it will appear here.</p>
          <Link to="/products" className="ui-btn ui-btn--primary">Browse products</Link>
        </div>
      )}

      {orders.length > 0 && (
        <ul className="ord-list" style={{ opacity: loading ? 0.6 : 1 }}>
          {orders.map((order) => {
            const items = orderItems(order);

            return (
              <li key={order.id} className="ord-card">
                <div className="ord-head">
                  <div className="ord-head-group">
                    <div className="ord-field">
                      <small>Order</small>
                      <strong>#{order.id}</strong>
                    </div>
                    <div className="ord-field">
                      <small>Placed</small>
                      <strong>{formatDateTime(order.created_at)}</strong>
                    </div>
                    <div className="ord-field">
                      <small>Total</small>
                      <strong>{formatMoney(order.total)}</strong>
                    </div>
                  </div>
                  <span className={`ui-badge ui-status--${order.status}`}>
                    {customerStatusLabel(order.status)}
                  </span>
                </div>

                <ul className="ord-items">
                  {items.map((item, i) => (
                    <li key={i} className="ord-item">
                      {item.image_url ? (
                        <img className="ord-thumb" src={item.image_url} alt="" loading="lazy" />
                      ) : (
                        <span className="ord-thumb ord-thumb--empty" aria-hidden="true">
                          {item.name?.charAt(0)}
                        </span>
                      )}
                      <div>
                        <div className="ord-item-name">{item.name}</div>
                        <div className="ord-item-meta">
                          {item.qty} x {formatMoney(item.price)}
                        </div>
                      </div>
                      <div className="ord-item-price">
                        {formatMoney(Number(item.price) * Number(item.qty))}
                      </div>
                    </li>
                  ))}
                </ul>

                {Number(order.delivery_fee) > 0 && (
                  <div className="ord-delivery">
                    <span>Delivery</span>
                    <span>{formatMoney(order.delivery_fee)}</span>
                  </div>
                )}

                <div className="ord-foot">
                  {canShowReceipt(order) ? (
                    <Link to={`/receipt/${order.id}`} className="ui-btn ui-btn--primary ui-btn--sm">
                      View receipt
                    </Link>
                  ) : (
                    <span className="ord-note">
                      {order.status === "amount_mismatch"
                        ? "We're checking this payment and will be in touch."
                        : "A receipt is available once payment is confirmed."}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}